import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ChevronDown, Minus, Plus } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { api } from "../api";
import { useCart } from "../context/CartContext";

const AccordionItem = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-zinc-800">
      <button
        data-testid={testid}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 mono text-xs tracking-[0.25em] uppercase text-zinc-300 hover:text-[#FF334B] transition-colors"
      >
        {title}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="pb-5 text-sm text-zinc-400 font-light leading-relaxed">{children}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function ProductPage() {
  const { slug } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState(null);
  const [qty, setQty] = useState(1);
  const [buying, setBuying] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => {
        setProduct(r.data);
        setSize(r.data.sizes[0]);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound)
    return (
      <main className="pt-40 pb-32 text-center" data-testid="product-not-found">
        <p className="text-zinc-500">This piece is not in the archive.</p>
        <Link to="/shop" className="mono text-xs tracking-[0.25em] uppercase text-[#FF334B] mt-4 inline-block">Back to catalog</Link>
      </main>
    );
  if (!product) return <main className="pt-40 pb-32 px-16 mono text-xs tracking-[0.25em] text-zinc-500 uppercase" data-testid="product-loading">Loading…</main>;

  const buyNow = async () => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: [{ slug: product.slug, qty, size }],
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed. Try again.");
      setBuying(false);
    }
  };

  return (
    <main data-testid="product-page" className="pt-16 min-h-screen">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="relative overflow-hidden bg-zinc-900 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)]">
          <motion.img
            key={product.slug}
            src={product.image}
            alt={product.name}
            className="w-full h-full object-cover aspect-[3/4] lg:aspect-auto"
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9 }}
          />
          <span className="absolute top-6 left-6 mono text-[10px] tracking-[0.25em] uppercase bg-black/70 backdrop-blur px-3 py-1.5 text-zinc-300">
            {product.tag}
          </span>
        </div>
        <div className="px-4 sm:px-8 lg:px-14 py-12 lg:py-20">
          <p className="mono text-xs tracking-[0.25em] text-[#FF334B] uppercase">{product.category}</p>
          <h1 data-testid="product-title" className="font-display font-extrabold tracking-tighter uppercase text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[0.95]">
            {product.name}
          </h1>
          <p data-testid="product-price" className="mono text-xl mt-5">${product.price.toFixed(0)} USD</p>
          <p className="text-zinc-400 font-light leading-relaxed mt-6 max-w-md">{product.description}</p>

          <div className="mt-10">
            <p className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase mb-3">Size</p>
            <div className="flex gap-2 flex-wrap">
              {product.sizes.map((s) => (
                <button
                  key={s}
                  data-testid={`size-pill-${s.toLowerCase().replace(" ", "-")}`}
                  onClick={() => setSize(s)}
                  className={`px-5 py-2.5 mono text-xs tracking-[0.15em] border transition-colors ${
                    size === s ? "border-[#FF334B] text-[#FF334B]" : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-8 flex items-center gap-4">
            <p className="mono text-[10px] tracking-[0.25em] text-zinc-500 uppercase">Qty</p>
            <div className="flex items-center gap-3 border border-zinc-700 px-3 py-2">
              <button data-testid="qty-decrease-button" onClick={() => setQty(Math.max(1, qty - 1))} className="hover:text-[#FF334B] transition-colors"><Minus size={14} /></button>
              <span data-testid="qty-value" className="mono text-sm w-6 text-center">{qty}</span>
              <button data-testid="qty-increase-button" onClick={() => setQty(Math.min(10, qty + 1))} className="hover:text-[#FF334B] transition-colors"><Plus size={14} /></button>
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="add-to-bag-button"
              onClick={() => add(product, size, qty)}
              className="flex-1 border border-zinc-500 py-4 mono text-xs tracking-[0.25em] uppercase hover:border-[#FF334B] hover:text-[#FF334B] transition-colors"
            >
              Add to Bag
            </button>
            <button
              data-testid="buy-now-button"
              onClick={buyNow}
              disabled={buying}
              className="flex-1 bg-[#FF334B] text-white py-4 mono text-xs tracking-[0.25em] uppercase hover:bg-[#d92339] transition-colors disabled:opacity-50"
            >
              {buying ? "Redirecting…" : "Buy Now"}
            </button>
          </div>

          <div className="mt-12">
            <AccordionItem title="Fabric & Care" testid="accordion-fabric">
              Premium materials sourced from our four partner mills. Cold wash, hang dry, never tumble. Leather pieces should be conditioned twice a year.
            </AccordionItem>
            <AccordionItem title="Fit & Sizing" testid="accordion-fit">
              Cut relaxed with architectural intent. If between sizes, take the smaller for a sharper line or the larger for drape. Model wears size M.
            </AccordionItem>
            <AccordionItem title="Shipping & Returns" testid="accordion-shipping">
              Free carbon-neutral shipping on orders over $150. 30-day returns on unworn pieces.{" "}
              <Link to="/refunds" data-testid="product-returns-link" className="text-[#FF334B] underline underline-offset-4">
                Read the full policy
              </Link>
              .
            </AccordionItem>
          </div>
        </div>
      </div>
    </main>
  );
}
